import { Table, Text, Group, Box } from '@mantine/core'
import { IconShieldCheck } from '@tabler/icons-react'
import { EmptyState } from '@/components/ui'
import { SeverityBadge } from '@/components/security/SeverityBadge'
import type { SecurityFinding } from '@/api/security'

function ageDays(firstSeen: string | null): number | null {
  if (!firstSeen) return null
  const d = new Date(firstSeen)
  if (isNaN(d.getTime())) return null
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000))
}

interface PriorityQueueProps {
  findings: SecurityFinding[]
  /** Categoria filtrada: a fila é a da categoria (todas as severidades). */
  category?: string | null
  onSelect?: (finding: SecurityFinding) => void
}

/** Fila de correção: sem categoria, os abertos do tenant; com categoria, os
 *  abertos dela — pior severidade primeiro nos dois casos. Sempre visível
 *  (EmptyState quando não há pendência) — a ausência de fila é informação. */
export function PriorityQueue({ findings, category, onSelect }: PriorityQueueProps) {
  if (findings.length === 0) {
    return category ? (
      <EmptyState
        icon={IconShieldCheck}
        size="sm"
        title={`Nenhum finding aberto em ${category}`}
        description="O último scan não deixou finding desta categoria sem resolver."
      />
    ) : (
      <EmptyState
        icon={IconShieldCheck}
        size="sm"
        title="Nenhum finding em aberto"
        description="O último scan não deixou finding sem resolver neste tenant."
      />
    )
  }

  return (
    <Table striped highlightOnHover withTableBorder data-testid="priority-queue">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Finding</Table.Th>
          <Table.Th>Severidade</Table.Th>
          <Table.Th>Categoria</Table.Th>
          <Table.Th>Recurso</Table.Th>
          <Table.Th>Em aberto</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {findings.map((f) => {
          const dias = ageDays(f.first_seen_at)
          return (
            <Table.Tr
              key={f.id}
              style={{ cursor: 'pointer' }}
              onClick={() => onSelect?.(f)}
            >
              <Table.Td><Text size="sm" lineClamp={1}>{f.title}</Text></Table.Td>
              <Table.Td><SeverityBadge severity={f.severity} size="sm" /></Table.Td>
              <Table.Td>
                <Text size="sm" ff="var(--mantine-font-family-monospace)">{f.category}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="xs" c="dimmed" lineClamp={1}
                      ff="var(--mantine-font-family-monospace)">
                  {f.resource_arn || f.resource_type || '—'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" ff="var(--mantine-font-family-monospace)">
                  {dias == null ? '—' : `${dias}d`}
                </Text>
              </Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </Table>
  )
}

/** Cabeçalho da fila — a faixa da página de `total` ("26–50 de 103"): a fila
 *  é paginada, e "25" sozinho lia como se fossem todos. */
export function PriorityQueueHeader({ offset = 0, shown, total }: { offset?: number; shown: number; total: number }) {
  return (
    <Group justify="space-between" mb="md">
      <Text size="sm" fw={600}>Fila de correção (prioridade)</Text>
      <Box>
        <Text size="sm" c="dimmed" ff="var(--mantine-font-family-monospace)">
          {total > shown ? `${offset + 1}–${offset + shown} de ${total}` : shown}
        </Text>
      </Box>
    </Group>
  )
}
